import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { eventToPart, makeBundle, PART_TYPES, PRICE_UNITS, priceWithFees } from "../components/BundleCard/bundleModel";
import { fetchTicketCategories } from "../services/eventTickets";
import {
  STAY_MODES,
  fetchVenuePlace,
  STAY_RADIUS_KM,
  boxAround,
  fetchStayOffers,
  offerToStayPart,
  stayWindowForEvent,
  nightsBetween,
} from "../services/eventStay";
import {
  fetchVenueAirports,
  fetchFlightOffers,
  groupFlightOffers,
  flightToPart,
} from "../services/eventTravel";
import { populatePost } from "./utils";

/**
 * Eventstrecke -- das Buendel im Aufbau.
 *
 * Der Reisende komponiert Schritt fuer Schritt: Event, dann Ticket, dann
 * Unterkunft, dann Anreise. Dieser Slice haelt genau diesen Zwischenstand.
 * Fertig ist er ein Buendel, das BundleCard ohne Zusatz darstellen kann.
 *
 * Konzept: systemdocumentation/architecture/KONZEPT_buendelkarte-und-bausteine.md
 */

export const EVENT_FLOW_STEPS = Object.freeze({
  TICKET: "ticket",
  STAY: "stay",
  TRAVEL: "travel",
  BASKET: "basket",
});

/** Vertragsgrenze RateHawk: ab 6 Zimmern ist die API-Buchung untersagt. */
export const MAX_ROOMS_VIA_API = 5;
const GUESTS_PER_ROOM = 2;

export { STAY_MODES };

/**
 * Schritt 2 -- Unterkunft ODER Pauschalreise.
 *
 * Beide Zweige suchen im selben Umkreis um die Venue und im selben Zeitraum;
 * nur der Modus entscheidet, ob der Flug im Vertrag steckt. Der Abflughafen
 * steht fest und kommt aus der Suche (state.search.stations) -- die Strecke
 * fragt ihn nicht noch einmal ab.
 */
export const loadStayOffers = createAsyncThunk(
  "eventFlow/loadStayOffers",
  async (_arg, thunkAPI) => {
    const state = thunkAPI.getState();
    const flow = state.eventFlow;
    const anchor = selectAnchorPart(state);

    const lat = Number(anchor?.anchorData?.lat);
    const lng = Number(anchor?.anchorData?.lng);
    // Ein Event ohne Venue-Koordinaten laesst sich nicht umkreisen. Das gehoert
    // benannt, nicht als "keine Treffer" getarnt.
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new Error("Für dieses Event ist kein Veranstaltungsort hinterlegt — eine Umkreissuche ist nicht möglich.");
    }

    const window = flow.stayWindow || stayWindowForEvent(anchor?.anchorData?.date);
    if (!window) throw new Error("Das Eventdatum ist unlesbar — der Reisezeitraum lässt sich nicht ableiten.");

    const box = boxAround(lat, lng, flow.stayRadiusKm || STAY_RADIUS_KM);
    const packageMode = flow.stayMode === STAY_MODES.PACKAGE;

    // Den Veranstaltungsort in beide Anbieter-Raeume aufloesen. Die Box allein
    // versteht nur Amadeus; ohne diesen Schritt sucht Peakwork ohne Ortsfilter
    // und liefert Treffer aus einer anderen Gegend. Die Strecke entscheidet
    // damit NICHT, wer die Suche bedient -- sie gibt allen dasselbe Ziel.
    // KEIN Fallback auf "dann eben nur die Box". Genau der hat die Liste
    // gefuellt: die Box versteht nur ein Teil der Anbieter, die uebrigen suchen
    // dann ohne Ortsfilter und liefern ihren allgemeinen Bestand. Am 03.09.2026
    // stand deshalb unter "Umkreis von Monza" eine Liste mit Hotels in Istanbul
    // und Alanya -- und ein falscher Ort faellt niemandem auf, der die Gegend
    // nicht kennt.
    //
    // Laesst sich der Veranstaltungsort nicht in einen Ort uebersetzen, den die
    // Anbieter kennen, gibt es hier kein Ergebnis. Lieber keine Liste als eine
    // aus dem falschen Land.
    // Aufgeloest wird ueber den ORTSNAMEN des Events, nicht ueber die
    // Venue-Koordinaten: die Zeilen in `locations` mit Anbieter-Kennung tragen
    // keine Koordinaten (siehe Kopf von fetchVenuePlace). Die Koordinaten
    // bleiben trotzdem noetig - aus ihnen entsteht die Box, mit der Amadeus
    // innerhalb des Ortes auf den Umkreis eingrenzt.
    const venueCity = anchor?.anchorData?.city;
    const venueCountry = anchor?.anchorData?.country;

    // Die Ortsaufloesung braucht NUR der Pauschalzweig: der geht an Anbieter,
    // von denen nicht jeder eine Bounding Box versteht -- wer sie nicht
    // versteht, sucht sonst ohne Ortsfilter und liefert eine andere Gegend.
    //
    // Der Unterkunftszweig sucht in unserem eigenen Bestand, und dort haengen
    // Koordinaten am Haus. Die Box genuegt also; ihn an der Ortsaufloesung
    // scheitern zu lassen, waere eine Huerde ohne Zweck.
    let venuePlace = null;
    if (packageMode) {
      try {
        venuePlace = await fetchVenuePlace(venueCity, venueCountry);
      } catch (err) {
        throw new Error(
          `Der Veranstaltungsort liess sich nicht bestimmen (${err?.message || "Ortsdienst nicht erreichbar"}). ` +
            "Eine Suche ohne bestimmten Ort wuerde Angebote aus anderen Regionen liefern."
        );
      }

      if (!venuePlace?.geo) {
        throw new Error(
          `Für „${venueCity || "diesen Veranstaltungsort"}" ist bei uns kein Ort hinterlegt, den die Anbieter kennen. ` +
            "Eine Umkreissuche ist deshalb nicht möglich — sie würde Angebote aus anderen Regionen liefern."
        );
      }
    }

    const body = populatePost(
      [],
      {
        ...state.search,
        // Die Box gewinnt in populatePost vor Region- und Hotel-IDs.
        southWest: box.southWest,
        northEast: box.northEast,
        travelDate: { from: window.from, until: window.until },
        duration: { value: String(window.nights), label: `${window.nights} Nächte` },
        // Die Dauer-Art ist je Zweig eine andere: fuer die reine Unterkunft
        // kennt die Suche NUR "OvernightStays" (utils.js
        // durationkindOptionsAccomodation), "Stay" und "Trip" gehoeren zur
        // Pauschal- und Flugseite. Wir haben hier vorher in beiden Zweigen
        // "Stay" geschickt -- im Hotelzweig eine Art, die es dort nicht gibt.
        durationkind: packageMode
          ? { value: "Trip", label: "Reisedauer" }
          : { value: "OvernightStays", label: "Übernachtungen" },
        // adults kommt unveraendert aus state.search (oben gespreadet) --
        // die Reisenden stehen in der Suchmaske links, nicht in der Strecke.
        children: { value: "0", label: "kein Kind" },
        // Verpflegung waehlt der Reisende in Schritt 2 selbst. Nur im
        // Pauschalzweig: dort ist die Verpflegung Teil der einen Leistung des
        // Veranstalters. Im Unterkunftszweig bleibt die Wahl der Suchmaske
        // ueberlassen, damit Schritt 2 dort nicht zwei Filter gegeneinander
        // stellt. Leere Liste = keine Einschraenkung; populatePost erwartet
        // IMMER ein Array und greift ungeprueft auf .length zu.
        boardType: packageMode ? flow.stayBoardTypes || [] : state.search?.boardType || [],
        activeMode: packageMode ? "PAC" : "ACC",
        promosession: state.system?.sessionId,
        offset: 0,
        amount: 30,
      },
      state.properties?.departureStations || []
    );

    const { offers, hotels, geo } = await fetchStayOffers(flow.stayMode, {
      ...body,
      // populatePost setzt `geo` nur im else-Zweig -- die Box gewinnt dort und
      // verdraengt es. Deshalb wird es hier an den fertigen Rumpf gehaengt:
      // beides gilt gleichzeitig, jeder Anbieter nimmt sich, was er versteht.
      // Ohne geo kommt die Suche gar nicht bis hierher (siehe oben).
      ...(venuePlace?.geo ? { geo: [venuePlace.geo] } : {}),
      mode: state.system?.mode,
      sortOrder: state.search?.sorting?.sorting,
    });

    return offers.map((offer) => offerToStayPart(offer, hotels, flow.stayMode, anchor, geo));
  }
);

/**
 * Schritt 3 -- die Anreise.
 *
 * Nur der Unterkunftszweig kommt hier an; die Pauschalreise hat den Flug schon
 * im Vertrag des Veranstalters.
 *
 * Der Zielflughafen wird nicht geraten: /locations/airports-for-place liefert
 * ihn zum Veranstaltungsort (Monza hat keinen eigenen -- dann die Flughaefen
 * seiner Region). Der Abflughafen steht in der Suchmaske und wird hier nicht
 * noch einmal erfragt.
 */
export const loadTravelOffers = createAsyncThunk(
  "eventFlow/loadTravelOffers",
  async (_arg, thunkAPI) => {
    const state = thunkAPI.getState();
    const flow = state.eventFlow;
    const anchor = selectAnchorPart(state);

    const from = state.search?.stations?.[0]?.value;
    if (!from) {
      throw new Error("In der Suche links steht kein Abflughafen — ohne ihn lässt sich kein Flug suchen.");
    }

    const window = flow.stayWindow;
    if (!window?.from || !window?.until) {
      throw new Error("Der Reisezeitraum fehlt — er entsteht in Schritt 2.");
    }

    // Zielflughafen bestimmen, sofern noch keiner gewaehlt wurde.
    let airports = flow.travelAirports;
    if (!airports || airports.length === 0) {
      const found = await fetchVenueAirports({
        name: anchor?.anchorData?.city,
        country: anchor?.anchorData?.country,
      });
      airports = found.items;
    }

    const to = flow.selectedAirport || airports?.[0]?.iata;
    if (!to) {
      throw new Error(
        `Für „${anchor?.anchorData?.city || "diesen Veranstaltungsort"}" ist kein Flughafen hinterlegt — eine Flugsuche ist nicht möglich.`
      );
    }

    const offers = await fetchFlightOffers({
      from,
      to,
      departDate: window.from,
      returnDate: window.until,
      adults: Number(state.search?.adults?.value) || 1,
    });

    return {
      airports,
      airport: to,
      parts: groupFlightOffers(offers).map(flightToPart),
    };
  }
);

export const loadTicketCategories = createAsyncThunk(
  "eventFlow/loadTicketCategories",
  async (_arg, thunkAPI) => {
    const anchor = selectAnchorPart(thunkAPI.getState());
    if (!anchor) throw new Error("Kein Event gewählt.");
    return fetchTicketCategories(anchor);
  }
);

const initialState = {
  active: false,
  step: EVENT_FLOW_STEPS.TICKET,
  parts: [],
  anchorId: null,
  // Kartenanzahl -- NICHT die Reisenden. Die Reisenden stehen in der
  // Suchmaske links (search.adults) und steuern Hotel und Zimmerbedarf; wie
  // viele Karten gekauft werden, ist davon unabhaengig.
  ticketCount: 2,
  categories: [],
  categoriesState: "idle", // idle | loading | ready | failed
  categoriesError: "",
  selectedCategoryId: null,

  // Schritt 2 -- die Weiche. null heisst: der Reisende hat noch nicht gewaehlt.
  stayMode: null, // STAY_MODES.HOTEL | STAY_MODES.PACKAGE
  stayRadiusKm: STAY_RADIUS_KM,
  stayWindow: null, // { from, until, nights } -- aus dem Eventdatum vorbelegt
  stayOffers: [],
  // Gewaehlte Verpflegungsarten (Werte aus boardTypes). Leer = alle.
  stayBoardTypes: [],
  stayState: "idle", // idle | loading | ready | failed
  stayError: "",
  selectedStayId: null,

  // Schritt 3 -- Anreise. Nur im Unterkunftszweig; die Pauschalreise hat den
  // Flug bereits im Vertrag des Veranstalters.
  travelAirports: [],
  selectedAirport: null,
  travelOffers: [],
  travelState: "idle", // idle | loading | ready | failed
  travelError: "",
  selectedTravelId: null,
  // Wer selbst anreist, hat keinen Flugvertrag -- und soll trotzdem zur
  // Uebersicht kommen. Ohne diesen Modus haengt der Schritt an einem
  // ausgewaehlten Flug fest, obwohl Ticket und Hotel laengst stehen; "kein
  // Flug ab BER" war damit eine Sackgasse. Bewusst ein eigener Zustand und
  // nicht "nichts ausgewaehlt": das eine ist eine Entscheidung, das andere
  // ein offener Schritt. Spaeter kommen hier Mietwagen und Bahn dazu.
  travelMode: "flight", // flight | own
};

const eventFlowSlice = createSlice({
  name: "eventFlow",
  initialState,
  reducers: {
    startEventFlow(state, { payload }) {
      const part = eventToPart(payload);
      state.active = true;
      state.step = EVENT_FLOW_STEPS.TICKET;
      state.parts = [part];
      state.anchorId = part.id;
      state.ticketCount = 2;
      state.categories = [];
      state.categoriesState = "idle";
      state.categoriesError = "";
      state.selectedCategoryId = null;
      state.stayMode = null;
      state.stayRadiusKm = STAY_RADIUS_KM;
      state.stayWindow = stayWindowForEvent(part.anchorData?.date);
      state.stayOffers = [];
      state.stayState = "idle";
      state.stayError = "";
      state.selectedStayId = null;
    },
    endEventFlow() {
      return initialState;
    },
    setStep(state, { payload }) {
      state.step = payload;
    },
    /**
     * Kartenanzahl setzen. `allowed` (optional) sind die Stueckzahlen, die der
     * Anbieter verkauft -- dann rastet die Auswahl darauf ein, statt eine Zahl
     * zuzulassen, die die Buchung spaeter ablehnt.
     */
    setTicketCount(state, { payload }) {
      const raw = typeof payload === "object" && payload !== null ? payload.value : payload;
      const allowed = typeof payload === "object" && payload !== null ? payload.allowed : null;
      const wanted = Math.max(1, Math.min(20, Number(raw) || 1));

      if (!Array.isArray(allowed) || allowed.length === 0) {
        state.ticketCount = wanted;
        return;
      }

      // Richtung beibehalten: wer "+" drueckt, soll nicht auf derselben Zahl
      // haengenbleiben, nur weil die naechste erlaubte weiter weg liegt.
      const up = wanted > state.ticketCount;
      const sorted = [...allowed].sort((a, b) => a - b);
      const next = up
        ? sorted.find((q) => q >= wanted)
        : [...sorted].reverse().find((q) => q <= wanted);

      state.ticketCount = next ?? (up ? sorted[sorted.length - 1] : sorted[0]);
    },
    selectCategory(state, { payload }) {
      state.selectedCategoryId = payload;

      // Jede Kategorie hat ihre eigenen Stueckzahlen. Nach dem Wechsel kann die
      // bisherige Anzahl unverkaeuflich sein -- dann still auf die naechste
      // erlaubte ziehen, statt die Buchung spaeter auflaufen zu lassen.
      const category = state.categories.find((c) => c.id === payload);
      const allowed = Array.isArray(category?.quantities) ? category.quantities : [];
      if (allowed.length > 0 && !allowed.includes(state.ticketCount)) {
        const sorted = [...allowed].sort((a, b) => a - b);
        state.ticketCount = sorted.find((q) => q >= state.ticketCount) ?? sorted[sorted.length - 1];
      }
    },
    /**
     * Weiche in Schritt 2. Der Wechsel verwirft die Treffer des anderen Zweigs
     * mitsamt Auswahl -- ein Pauschalangebot darf nicht als Unterkunft
     * stehenbleiben, wenn der Reisende umschwenkt.
     */
    /**
     * Reisezeitraum in Schritt 2. Der Reisende darf ihn aendern -- die
     * Vorbelegung aus dem Eventdatum ist ein Vorschlag, keine Vorgabe.
     *
     * Die Naechte werden NICHT getrennt gefuehrt, sondern aus den beiden Daten
     * gerechnet: zwei Zustaendige fuer dieselbe Angabe laufen auseinander.
     *
     * Der Eventtag muss im Zeitraum liegen -- ein Hotel fuer Naechte zu
     * suchen, in denen man gar nicht da ist, ist kein sinnvoller Wunsch,
     * sondern ein Vertipper.
     */
    setStayWindow(state, { payload }) {
      const current = state.stayWindow || {};
      const from = payload?.from || current.from;
      const until = payload?.until || current.until;
      if (!from || !until || until <= from) return;

      const day = current.eventDay;
      if (day && (from > day || until < day)) return;

      state.stayWindow = { from, until, nights: nightsBetween(from, until), eventDay: day };
      // Der bisherige Treffer gehoert zu einem anderen Zeitraum -- er darf
      // nicht stehenbleiben, als gaelte er weiter.
      state.stayOffers = [];
      state.selectedStayId = null;
      state.stayState = "idle";
      state.stayError = "";
    },
    setStayMode(state, { payload }) {
      if (state.stayMode === payload) return;
      state.stayMode = payload;
      state.stayOffers = [];
      state.stayState = "idle";
      state.stayError = "";
      state.selectedStayId = null;
    },
    /**
     * Verpflegungsart an- oder abwaehlen (Mehrfachauswahl).
     *
     * Die bisherigen Angebote gehoeren zu einer anderen Einschraenkung und
     * werden verworfen -- stehenzulassen hiesse, eine alte Liste als Ergebnis
     * der neuen Wahl auszugeben.
     */
    toggleStayBoardType(state, { payload }) {
      const list = state.stayBoardTypes || [];
      state.stayBoardTypes = list.includes(payload)
        ? list.filter((v) => v !== payload)
        : [...list, payload];
      state.stayOffers = [];
      state.stayState = "idle";
      state.stayError = "";
      state.selectedStayId = null;
    },
    /** Zielflughafen wechseln -- die bisherigen Fluege gelten dann nicht mehr. */
    selectAirport(state, { payload }) {
      if (state.selectedAirport === payload) return;
      state.selectedAirport = payload;
      state.travelOffers = [];
      state.travelState = "idle";
      state.travelError = "";
      state.selectedTravelId = null;
    },
    selectTravel(state, { payload }) {
      state.selectedTravelId = payload;
      state.travelMode = "flight";
    },
    setTravelMode(state, { payload }) {
      state.travelMode = payload === "own" ? "own" : "flight";
      // Eine vorher getroffene Flugwahl faellt weg, sonst stuende sie
      // unsichtbar in der Uebersicht und im Vorgang.
      if (state.travelMode === "own") state.selectedTravelId = null;
    },
    setStayRadius(state, { payload }) {
      state.stayRadiusKm = Math.max(1, Math.min(100, Number(payload) || STAY_RADIUS_KM));
      state.stayState = "idle";
    },
    selectStay(state, { payload }) {
      state.selectedStayId = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTicketCategories.pending, (state) => {
        state.categoriesState = "loading";
        state.categoriesError = "";
      })
      .addCase(loadTicketCategories.fulfilled, (state, { payload }) => {
        state.categories = payload || [];
        state.categoriesState = "ready";
      })
      .addCase(loadTicketCategories.rejected, (state, action) => {
        // Kein stiller Fallback auf eine leere Liste: der Grund gehoert an den
        // Reisenden, sonst sieht "Anbieter down" aus wie "ausverkauft".
        state.categories = [];
        state.categoriesState = "failed";
        state.categoriesError = action.error?.message || "Kategorien nicht abrufbar.";
      })
      .addCase(loadStayOffers.pending, (state) => {
        state.stayState = "loading";
        state.stayError = "";
      })
      .addCase(loadStayOffers.fulfilled, (state, { payload }) => {
        state.stayOffers = payload || [];
        state.stayState = "ready";
      })
      .addCase(loadStayOffers.rejected, (state, action) => {
        // Wie bei den Tickets: der Grund gehoert an den Reisenden. "Provider
        // nicht erreichbar" darf nicht wie "ausgebucht" aussehen.
        state.stayOffers = [];
        state.stayState = "failed";
        state.stayError = action.error?.message || "Angebote nicht abrufbar.";
      })

      .addCase(loadTravelOffers.pending, (state) => {
        state.travelState = "loading";
        state.travelError = "";
      })
      .addCase(loadTravelOffers.fulfilled, (state, { payload }) => {
        state.travelAirports = payload.airports || [];
        state.selectedAirport = payload.airport || null;
        state.travelOffers = payload.parts || [];
        state.travelState = "ready";
      })
      .addCase(loadTravelOffers.rejected, (state, action) => {
        // Auch hier: der Grund gehoert an den Reisenden. "Kein Flughafen
        // hinterlegt" ist etwas anderes als "keine Fluege frei".
        state.travelOffers = [];
        state.travelState = "failed";
        state.travelError = action.error?.message || "Flüge nicht abrufbar.";
      });
  },
});

export const {
  startEventFlow,
  endEventFlow,
  setStep,
  setTicketCount,
  selectCategory,
  setStayMode,
  setStayWindow,
  setStayRadius,
  toggleStayBoardType,
  selectStay,
  selectAirport,
  selectTravel,
  setTravelMode,
} = eventFlowSlice.actions;

export default eventFlowSlice.reducer;

// ---------- Selektoren ----------

export const selectFlow = (state) => state.eventFlow;

export const selectAnchorPart = (state) => {
  const flow = state.eventFlow;
  return flow.parts.find((p) => p.id === flow.anchorId) || flow.parts[0] || null;
};

export const selectSelectedStay = (state) =>
  state.eventFlow.stayOffers.find((p) => p.id === state.eventFlow.selectedStayId) || null;

/** Im Pauschal-Zweig steckt der Flug im Vertrag -- Schritt 3 entfaellt dann. */
export const selectSelectedTravel = (state) =>
  state.eventFlow.travelOffers.find((t) => t.id === state.eventFlow.selectedTravelId) || null;

/** Anreise ist geklaert: entweder ein Flug gewaehlt oder selbst organisiert. */
export const selectTravelSettled = (state) =>
  state.eventFlow.travelMode === "own" || Boolean(selectSelectedTravel(state));

export const selectNeedsTravelStep = (state) =>
  state.eventFlow.stayMode === STAY_MODES.HOTEL;

export const selectSelectedCategory = (state) =>
  state.eventFlow.categories.find((c) => c.id === state.eventFlow.selectedCategoryId) || null;

/**
 * Kategorien zu Gruppen zusammenfassen.
 *
 * SE365 liefert dieselbe Leistung mehrfach zu verschiedenen Preisen -- Monza
 * fuehrt "Tagesrennen / Vedano (24) / Alle zusammen" sechsmal zwischen 301 und
 * 478 EUR. Sechs ununterscheidbare Zeilen sind fuer den Reisenden kein Angebot,
 * sondern Laerm.
 *
 * Gruppiert wird deshalb NUR, was sich fuer ihn wirklich nicht unterscheidet:
 * gleicher Name, gleiche Beschreibung, gleiche Merkmale. Sobald der Anbieter
 * irgendetwas Unterscheidendes liefert, bleiben die Zeilen getrennt stehen --
 * das ist der Grund fuer den ausfuehrlichen Schluessel unten.
 *
 * Die Reihenfolge der Kategorien bleibt erhalten: die Gruppe erscheint dort,
 * wo ihr guenstigstes Angebot stand.
 */
export const selectCategoryGroups = (state) => {
  const groups = [];
  const byKey = new Map();

  for (const category of state.eventFlow.categories) {
    const key = [
      category.name || "",
      category.note || "",
      category.isVip ? "vip" : "",
      (category.restrictions || []).join("|"),
      category.onHold24h ? "hold" : "",
      // Wie viele Plaetze nebeneinander liegen, ist ein echter Unterschied --
      // wer zu zweit sitzen will, kauft nicht dasselbe wie ein Einzelplatz.
      (category.seatingFlags || []).join("+"),
    ].join("§");

    let group = byKey.get(key);
    if (!group) {
      group = { key, sample: category, entries: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.entries.push(category);
  }

  return groups.map((group) => {
    // Innerhalb der Gruppe nach Preis -- bei gleicher Leistung ist der
    // guenstigste der einzige, der den Reisenden interessiert.
    const sorted = [...group.entries].sort((a, b) => totalPrice(a) - totalPrice(b));

    // Angebote aussortieren, die von einem anderen in JEDER Hinsicht
    // geschlagen werden: teurer (oder gleich teuer) und dabei keine
    // Stueckzahl, die es nicht auch guenstiger gaebe.
    //
    // Warum das sein muss: SE365 fuehrt fuer ununterscheidbare Kontingente
    // verschiedene Preise. Zeigen wir beide, fragt der Reisende zu Recht,
    // wofuer er 25 EUR mehr zahlen soll -- und wir haben keine Antwort. Ein
    // Angebot, das nichts kann, was ein billigeres nicht auch kann, ist keine
    // Wahlmoeglichkeit, sondern eine Zumutung.
    const entries = sorted.filter((candidate, i) =>
      !sorted.some((other, j) => j < i && dominates(other, candidate))
    );
    const hidden = sorted.length - entries.length;
    const priced = entries.filter((e) => e.price > 0);
    const cheapest = priced[0] || null;

    return {
      key: group.key,
      sample: group.sample,
      entries,
      count: entries.length,
      // Wie viele gleichwertige, aber teurere Kontingente ausgeblendet sind.
      // Nicht verschwiegen: die Gruppe sagt es, wenn sie aufgeklappt ist.
      hidden,
      // Ohne Preis gibt es kein "ab" -- 0 waere hier eine Falschaussage.
      fromPrice: cheapest ? cheapest.price + (cheapest.serviceFee || 0) : null,
      currency: cheapest?.currency || "EUR",
      // Nur echte Mehrfachangebote werden geklappt.
      collapsible: entries.length > 1,
    };
  });
};

/** Was der Reisende je Person zahlt -- Servicegebuehr eingerechnet. */
function totalPrice(category) {
  return (category.price || 0) + (category.serviceFee || 0);
}

/**
 * Schlaegt `a` das Angebot `b` in jeder Hinsicht?
 *
 * Das ist der Fall, wenn a hoechstens so teuer ist UND jede Stueckzahl
 * abdeckt, die b abdeckt. Dann gibt es keinen Grund, b zu waehlen.
 * Deckt b eine Stueckzahl ab, die a nicht hat, bleibt b stehen -- wer vier
 * Karten zusammen braucht, hat sonst ploetzlich kein Angebot mehr.
 */
function dominates(a, b) {
  if (totalPrice(a) > totalPrice(b)) return false;
  const mine = new Set(a.quantities || []);
  const theirs = b.quantities || [];
  if (theirs.length === 0) return (a.quantities || []).length === 0;
  return theirs.every((q) => mine.has(q));
}

/**
 * Welche Stueckzahlen der Ticketanbieter ueberhaupt verkauft.
 *
 * SE365 verkauft nicht jede Zahl -- oft nur Paare. Leere Liste heisst "keine
 * Einschraenkung bekannt", nicht "nichts erlaubt".
 */
export const selectAllowedQuantities = (state) => {
  const category = selectSelectedCategory(state);
  const list = category?.quantities;
  return Array.isArray(list) && list.length > 0 ? list : null;
};

/**
 * Reisende -- aus der Suchmaske links, NICHT aus der Strecke.
 *
 * Sie steuern Hotel und Zimmerbedarf. Die Kartenanzahl ist davon unabhaengig
 * und steht als ticketCount am Ticketbaustein.
 */
export const selectTravellers = (state) => {
  const n = Number(state.search?.adults?.value);
  return n > 0 ? n : 1;
};

/** Zimmerbedarf und die daraus folgende Vertragsgrenze. */
export const selectRoomGate = (state) => {
  const rooms = Math.ceil(selectTravellers(state) / GUESTS_PER_ROOM);
  return {
    rooms,
    exceeded: rooms > MAX_ROOMS_VIA_API,
    limit: MAX_ROOMS_VIA_API,
  };
};

/** Das Buendel, wie BundleCard es erwartet -- inklusive gewaehltem Ticket. */
export const selectBundle = (state) => {
  const flow = state.eventFlow;
  const category = selectSelectedCategory(state);
  const gate = selectRoomGate(state);
  const travellers = selectTravellers(state);

  const parts = flow.parts.map((part) => {
    if (part.type !== PART_TYPES.EVENT || !category) return part;
    return {
      ...part,
      lines: [...part.lines, category.name],
      // Eigene Menge am Baustein: das Ticket rechnet mit den Karten, Hotel und
      // Flug mit den Reisenden.
      quantity: flow.ticketCount,
      // Servicegebuehr gehoert in den Preis, den der Reisende zahlt -- und
      // zugleich sichtbar in fees. Sie wegzulassen hiesse, einen zu niedrigen
      // Ticketpreis zu zeigen.
      price: priceWithFees(
        category.price,
        [{ label: "Servicegebühr", amount: category.serviceFee || 0 }],
        category.currency,
        PRICE_UNITS.PER_PERSON,
        false
      ),
      constraints: gate.exceeded
        ? [
            {
              kind: "group_size",
              severity: "warning",
              text: `${travellers} Reisende ergeben ${gate.rooms} Zimmer. Ab ${gate.limit + 1} Zimmern ist die Unterkunft nicht über die Schnittstelle buchbar — Tickets ja, Hotel über die Agentur.`,
            },
          ]
        : [],
    };
  });

  // Die gewaehlten Bausteine aus Schritt 2 und 3 kommen hinten dran. Sie
  // ersetzen nichts und werden nicht mit dem Ticket verrechnet -- getrennte
  // Vertraege, getrennte Preise, die Karte bildet daraus eine Summe.
  //
  // Der Flug steht bewusst als eigener Baustein neben dem Hotel und nicht mit
  // ihm zusammengefasst: es sind zwei Vertraege mit zwei Anbietern. Genau
  // daran haengt der Unterschied zwischen verbundenen Reiseleistungen und
  // einer Pauschalreise.
  const stay = selectSelectedStay(state);
  const travel = selectSelectedTravel(state);

  const chosen = [...parts];
  if (stay) chosen.push(stay);
  if (travel) chosen.push(travel);

  return makeBundle(chosen, {
    anchorId: flow.anchorId,
    travellers,
  });
};
